import {Injectable}                       from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanMatch,
    Route,
    Router,
    RouterStateSnapshot,
    UrlSegment,
    UrlTree,
}                                         from '@angular/router';
import {AuthService}                      from '@src/app/core/services/auth.service';
import {BillingFeature}                   from '@src/app/core/enums/billing-feature.enum';
import {BillingPolicyService}             from '@src/app/core/services/billing-policy.service';
import {FeatureFlagService}               from '@src/app/core/services/feature-flag.service';
import {UsersService}                     from '@src/app/admin/users/services/users.service';
import {from, Observable, of}             from 'rxjs';
import {catchError, map, switchMap, take} from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class FeatureFlagGuard implements CanActivate, CanMatch
{
    constructor(
        private readonly featureFlagService: FeatureFlagService,
        private readonly usersService: UsersService,
        private readonly authService: AuthService,
        private readonly billingPolicyService: BillingPolicyService,
        private readonly router: Router,
    )
    {
    }

    public canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree
    {
        return this.check(route.data?.featureFlag, state.url);
    }

    public canMatch(
        route: Route,
        segments: UrlSegment[],
    ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree
    {
        return this.check(route.data?.featureFlag, `/app/${segments.map(segment => segment.path).join('/')}`);
    }

    private check(flagKey: string | undefined, redirectUrl: string): Observable<boolean | UrlTree>
    {
        if (!flagKey) {
            return of(true);
        }

        const user = this.usersService.user;
        const tenant = this.authService.tenant;

        return from(this.featureFlagService.initialize(user, tenant)).pipe(
            switchMap(() => this.featureFlagService.getFlag(flagKey)),
            take(1),
            catchError(() => of(false)),
            map((enabled) => {
                if (Boolean(enabled)) return true;
                const billingFeature = BillingFeature[flagKey as keyof typeof BillingFeature] ?? flagKey;
                return this.billingPolicyService.isFeatureAllowed(billingFeature)
                    ? this.router.createUrlTree(['/app'], {queryParams: {redirectUrl}})
                    : this.router.createUrlTree(['/app/settings/7'], {queryParams: {access_unavailable: true, redirectUrl}});
            }),
        );
    }
}
